import React, { useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Typography } from '@material-ui/core';
import Icon from '@material-ui/core/Icon';
import Skeleton from '@material-ui/lab/Skeleton';
import { formatDistanceToNow } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import clsx from 'clsx';
import { isValid } from 'helpers/Date';

import { getActivityConfig } from 'app/main/dashboard/constants';
import * as DashboardActions from '../../store/actions';
import SectionHeader from './SectionHeader';

const RECENT_PATIENT_ACTIVITY_VISIBLE_ITEMS = 5;

const toRelativeTimeString = (date) => {
  if (!date) return '';

  const targetDate = typeof date === 'string' ? new Date(date) : date;
  if (!isValid(targetDate)) return '';

  return formatDistanceToNow(targetDate, { addSuffix: true });
};

const useStyles = makeStyles(() => ({
  activitySection: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
    overflow: 'hidden',
    gap: 2,
  },
  scrollableList: {
    overflowY: 'auto',
    minHeight: 0,
    scrollbarWidth: 'thin',
    scrollbarColor: '#BFE3F3 transparent',
    '&::-webkit-scrollbar': {
      width: 6,
    },
    '&::-webkit-scrollbar-track': {
      background: 'transparent',
    },
    '&::-webkit-scrollbar-thumb': {
      background: '#BFE3F3',
      borderRadius: 4,
    },
    '&::-webkit-scrollbar-thumb:hover': {
      background: '#0B9FD9',
    },
  },
  activityScrollArea: {
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
  },
  activityList: {
    position: 'relative',
    paddingLeft: 28,
  },
  activityTimeline: {
    position: 'absolute',
    left: 11,
    top: 14,
    bottom: 14,
    width: 2,
    background: '#E4EDF3',
  },
  activityItem: {
    position: 'relative',
    paddingBottom: 16,
    '&:last-child': {
      paddingBottom: 0,
    },
  },
  activityIcon: {
    position: 'absolute',
    left: -28,
    top: 0,
    width: 24,
    height: 24,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#FFFFFF',
    zIndex: 1,
  },
  activityIconGlyph: {
    fontSize: 14,
  },
  activityHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 8,
    marginBottom: 4,
  },
  activityLabel: {
    fontWeight: 600,
    fontSize: 13,
    lineHeight: 1.35,
  },
  activityMeta: {
    color: '#9BB0BB',
    fontSize: 11,
    whiteSpace: 'nowrap',
    flexShrink: 0,
    textAlign: 'right',
    lineHeight: 1.5,
  },
  activityDetail: {
    color: '#486470',
    fontSize: 13,
    lineHeight: 1.4,
  },
  activityPatientName: {
    fontWeight: 600,
    color: '#18323F',
  },
  emptyState: {
    flex: 1,
    minHeight: 0,
    padding: '4px 4px 8px',
  },
  emptyText: {
    color: '#9BB0BB',
    fontSize: 13,
    lineHeight: 1.5,
  },
  skeletonItem: {
    display: 'flex',
    alignItems: 'flex-start',
    paddingBottom: 16,
  },
  skeletonBody: {
    flex: 1,
    minWidth: 0,
  },
}));

function RecentPatientActivityItem({ activity }) {
  const classes = useStyles();
  const { t } = useTranslation();
  const config = getActivityConfig(activity.state);
  const relativeTime = toRelativeTimeString(activity.changeDate);
  const userName = activity.userFullName || activity.userName || '';

  return (
    <div className={classes.activityItem}>
      <div className={classes.activityIcon} style={{ backgroundColor: config.color }}>
        <Icon className={classes.activityIconGlyph}>{config.icon}</Icon>
      </div>
      <div className={classes.activityHeader}>
        <Typography className={clsx(classes.activityLabel, 'cursor-default')} style={{ color: config.color }}>
          {t(config.labelKey)}
        </Typography>
        {(relativeTime || userName) && (
          <div className={classes.activityMeta}>
            {relativeTime && (
              <Typography className="cursor-default" component="div">
                {relativeTime}
              </Typography>
            )}
            {userName && (
              <Typography className="cursor-default" component="div">
                {t('dashboard:PATIENT_ACTIVITY_BY_USER', { user: userName })}
              </Typography>
            )}
          </div>
        )}
      </div>
      {(activity.patientName || activity.drugName) && (
        <Typography className={clsx(classes.activityDetail, 'cursor-default')}>
          {activity.patientName && <span className={classes.activityPatientName}>{activity.patientName}</span>}
          {activity.patientName && activity.drugName && ' - '}
          {activity.drugName}
        </Typography>
      )}
    </div>
  );
}

function RecentPatientActivitySkeleton({ count = RECENT_PATIENT_ACTIVITY_VISIBLE_ITEMS }) {
  const classes = useStyles();

  return (
    <>
      {Array.from({ length: count }, (_, i) => (
        <div key={i} className={classes.skeletonItem}>
          <Skeleton variant="circle" width={24} height={24} style={{ marginRight: 12, flexShrink: 0 }} />
          <div className={classes.skeletonBody}>
            <Skeleton variant="text" width="50%" height={18} />
            <Skeleton variant="text" width="90%" height={16} />
          </div>
        </div>
      ))}
    </>
  );
}

const getActivityItemKey = (activity, index) =>
  [activity?.changeDate, activity?.patientName, activity?.drugName, activity?.state, activity?.user, index].join('|');

function RecentPatientActivityList({ activities }) {
  const classes = useStyles();

  return (
    <div className={classes.activityList}>
      <div className={classes.activityTimeline} />
      {activities.map((activity, index) => (
        <RecentPatientActivityItem key={getActivityItemKey(activity, index)} activity={activity} />
      ))}
    </div>
  );
}

function RecentPatientActivitySection() {
  const classes = useStyles();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { data: activities, loading } = useSelector((state) => state.dashboard.notice.recentPatientActivity);
  const { currentPharmacyId, id: userId } = useSelector((state) => state.auth.user.data);

  useEffect(() => {
    if (!currentPharmacyId || !userId) return;
    dispatch(DashboardActions.getRecentPatientActivity());
  }, [dispatch, currentPharmacyId, userId]);

  return (
    <div className={classes.activitySection}>
      <SectionHeader title={t('dashboard:RECENT_PATIENT_ACTIVITY_TITLE')} />
      <div className={clsx(classes.activityScrollArea, classes.scrollableList)}>
        {loading ? (
          <RecentPatientActivitySkeleton />
        ) : activities.length > 0 ? (
          <RecentPatientActivityList activities={activities} />
        ) : (
          <div className={classes.emptyState}>
            <Typography className={clsx(classes.emptyText, 'cursor-default')}>
              {t('dashboard:PATIENT_ACTIVITY_EMPTY')}
            </Typography>
          </div>
        )}
      </div>
    </div>
  );
}

export default RecentPatientActivitySection;
